import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  List, ListItem, ListItemText, Divider, Table, TableBody,
  TableRow, TableCell, Chip, Grid, Accordion, AccordionSummary,
  AccordionDetails, Checkbox, Collapse, ToggleButton, ToggleButtonGroup,
  Autocomplete, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import { searchShadowMinistryRecords } from '../services/api';
import YearRangeSlider from './YearRangeSlider';

const currentYear = new Date().getFullYear();

export default function ShadowMinistryRecordsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [activeFilters, setActiveFilters] = useState({});
  const [computedFacets, setComputedFacets] = useState({});

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    name: '',
    ministry: '',
    role: '',
    chamber: ''
  });
  const [roleYearRange, setRoleYearRange] = useState([1901, currentYear]);

  // Available options for autocomplete
  const [availableOptions, setAvailableOptions] = useState({
    names: [],
    ministries: [],
    roles: []
  });

  // Fetch autocomplete options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await searchShadowMinistryRecords('*', 1, 100);
        if (data.hits) {
          const names = [...new Set(data.hits.map(h => h.DisplayName).filter(Boolean))].sort();
          const ministries = [...new Set(data.hits.map(h => h.Ministry).filter(Boolean))].sort();
          const roles = [...new Set(data.hits.map(h => h.Role).filter(Boolean))].sort();
          setAvailableOptions({ names, ministries, roles });
        }
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery && urlQuery !== '*') {
      handleSearch(urlQuery);
    } else {
      handleSearch('*');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computeFacetsFromResults = (hits) => {
    const facets = {
      ministries: {},
      roles: {},
      chambers: {}
    };

    hits.forEach(hit => {
      if (hit.Ministry) {
        facets.ministries[hit.Ministry] = (facets.ministries[hit.Ministry] || 0) + 1;
      }
      if (hit.Role) {
        facets.roles[hit.Role] = (facets.roles[hit.Role] || 0) + 1;
      }
      if (hit.MPorSenator) {
        facets.chambers[hit.MPorSenator] = (facets.chambers[hit.MPorSenator] || 0) + 1;
      }
    });

    const result = {};
    Object.entries(facets).forEach(([name, counts]) => {
      result[name] = Object.entries(counts)
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 20);
    });
    return result;
  };

  const handleChange = (field) => (event) => {
    setForm({ ...form, [field]: event.target.value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.name) parts.push(`DisplayName:"${form.name}"`);
    if (form.ministry) parts.push(`Ministry:"${form.ministry}"`);
    if (form.role) parts.push(`Role:"${form.role}"`);
    if (form.chamber) parts.push(`MPorSenator:"${form.chamber}"`);

    if (roleYearRange[0] > 1901 || roleYearRange[1] < currentYear) {
      parts.push(`RDateStart:[${roleYearRange[0]}-01-01 TO ${roleYearRange[1]}-12-31]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldName = field === 'ministries' ? 'Ministry' : field === 'roles' ? 'Role' : 'MPorSenator';
        const valueParts = values.map(v => `${fieldName}:"${v}"`).join(' OR ');
        filterParts.push(`(${valueParts})`);
      }
    });

    if (filterParts.length === 0) return baseQuery;
    if (baseQuery === '*') return filterParts.join(' AND ');
    return `(${baseQuery}) AND ${filterParts.join(' AND ')}`;
  };

  const handleSearch = async (searchQuery = null, filters = activeFilters) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = searchQuery !== null ? searchQuery : (query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    setLoading(true);
    try {
      const fullQuery = buildQueryWithFilters(baseQuery, filters);
      const data = await searchShadowMinistryRecords(fullQuery, 1, 100);
      setResults(data);
      const facets = computeFacetsFromResults(data.hits);
      setComputedFacets(facets);
      setSearchParams({ q: baseQuery, mode: searchMode });
    } catch (error) {
      console.error('Search error:', error);
    }
    setLoading(false);
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      name: '',
      ministry: '',
      role: '',
      chamber: ''
    });
    setRoleYearRange([1901, currentYear]);
    setActiveFilters({});
    setResults(null);
    setSearchParams({});
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) newFilters[field] = [];
    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) delete newFilters[field];
    } else {
      newFilters[field].push(value);
    }
    setActiveFilters(newFilters);
    handleSearch(null, newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, {});
  };

  const toggleDetails = (id) => {
    setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const MetadataRow = ({ label, value }) => {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return null;
    return (
      <TableRow>
        <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '30%', py: 1 }}>{label}</TableCell>
        <TableCell sx={{ py: 1 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</TableCell>
      </TableRow>
    );
  };

  const buildRoleTitle = (hit) => {
    let title = hit.Role || '';
    if (hit.Prep) title += ` ${hit.Prep}`;
    if (hit.Entity) title += ` ${hit.Entity}`;
    return title.trim();
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Shadow Ministry Records</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Search shadow ministry appointment records..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                placeholder="Search across all fields"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.names}
                value={form.name}
                onChange={handleAutocompleteChange('name')}
                renderInput={(params) => <TextField {...params} label="Person Name" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.ministries}
                value={form.ministry}
                onChange={handleAutocompleteChange('ministry')}
                renderInput={(params) => <TextField {...params} label="Shadow Ministry" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.roles}
                value={form.role}
                onChange={handleAutocompleteChange('role')}
                renderInput={(params) => <TextField {...params} label="Role" placeholder="e.g., Shadow Minister" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Chamber</InputLabel>
                <Select value={form.chamber} onChange={handleChange('chamber')} label="Chamber">
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Member">Member (House)</MenuItem>
                  <MenuItem value="Senator">Senator</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <YearRangeSlider
                label="Role Start Year"
                value={roleYearRange}
                onChange={setRoleYearRange}
                min={1901}
                max={currentYear}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {Object.keys(computedFacets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, position: 'sticky', top: 16 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters}>Clear All</Button>
                  )}
                </Box>

                {Object.entries(computedFacets).map(([facetName, facetValues]) => (
                  facetValues.length > 0 && (
                    <Accordion key={facetName} defaultExpanded={facetName !== 'ministries'}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                          {facetName.replace('_', ' ')}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0, maxHeight: 200, overflow: 'auto' }}>
                          {facetValues.map((facet) => (
                            <ListItem key={facet.value} disablePadding>
                              <Checkbox
                                size="small"
                                checked={activeFilters[facetName]?.includes(facet.value) || false}
                                onChange={() => handleFacetClick(facetName, facet.value)}
                              />
                              <ListItemText
                                primary={`${facet.value} (${facet.count})`}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={Object.keys(computedFacets).length > 0 ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {results.total} shadow ministry records found
              </Typography>
              <List>
                {results.hits.map((hit, index) => (
                  <React.Fragment key={hit.id}>
                    {index > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ flexDirection: 'column' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="resultTitle">
                              {hit.DisplayName}
                            </Typography>
                            <Chip label={hit.Role} size="small" color="secondary" variant="outlined" />
                            {hit.MPorSenator && (
                              <Chip label={hit.MPorSenator} size="small" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              {buildRoleTitle(hit)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {hit.Ministry} ({formatDate(hit.RDateStart)} - {formatDate(hit.RDateEnd) || 'Present'})
                            </Typography>
                          </Box>
                        }
                      />
                      <Button size="small" onClick={() => toggleDetails(hit.id)} sx={{ mt: 1 }}>
                        {expandedDetails[hit.id] ? 'Hide details' : 'Show all details'}
                      </Button>
                      <Collapse in={expandedDetails[hit.id]}>
                        <Box sx={{ mt: 2, width: '100%' }}>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Appointment Details
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Person" value={hit.DisplayName} />
                              <MetadataRow label="PHID" value={hit.PHID} />
                              <MetadataRow label="Role" value={hit.Role} />
                              <MetadataRow label="Position" value={buildRoleTitle(hit)} />
                              <MetadataRow label="Chamber" value={hit.MPorSenator} />
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Shadow Ministry Information
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Shadow Ministry" value={hit.Ministry} />
                              <MetadataRow label="Shadow Ministry ID" value={hit.SMID} />
                              <MetadataRow label="Ministry Start" value={formatDate(hit.MDateStart)} />
                              <MetadataRow label="Ministry End" value={formatDate(hit.MDateEnd)} />
                              <TableRow>
                                <TableCell colSpan={2} sx={{ bgcolor: '#f5f5f5', fontWeight: 600 }}>
                                  Role Dates
                                </TableCell>
                              </TableRow>
                              <MetadataRow label="Role Start" value={formatDate(hit.RDateStart)} />
                              <MetadataRow label="Role End" value={formatDate(hit.RDateEnd)} />
                            </TableBody>
                          </Table>
                          <Box sx={{ mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              component={RouterLink}
                              to={`/parliamentarians?q=PHID:"${hit.PHID}"`}
                            >
                              View Parliamentarian
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
